import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Search, ListChecks, Settings as SettingsIcon, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useT } from "@/i18n/LanguageContext";

const Logo = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21V10.5a1 1 0 0 1 .4-.8l8-6a1 1 0 0 1 1.2 0l8 6a1 1 0 0 1 .4.8V21" />
    <path d="M3 21h18" />
    <path d="M7 21v-6h10v6" />
    <circle cx="12" cy="11" r="1.5" />
  </svg>
);

const useNav = () => {
  const t = useT();
  return [
    { to: "/", label: t("nav_dashboard"), icon: LayoutDashboard, testid: "sidebar-nav-dashboard-link" },
    { to: "/search", label: t("nav_search"), icon: Search, testid: "sidebar-nav-search-link" },
    { to: "/lists", label: t("nav_lists"), icon: ListChecks, testid: "sidebar-nav-lists-link" },
    { to: "/settings", label: t("nav_settings"), icon: SettingsIcon, testid: "sidebar-nav-settings-link" },
  ];
};

const NavItems = ({ onNavigate }) => {
  const nav = useNav();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-secondary text-foreground shadow-sm"
                : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => {
  const t = useT();
  return (
    <div className="flex items-center gap-2.5 px-5 py-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Logo />
      </div>
      <div>
        <div className="font-heading text-lg font-bold leading-none tracking-tight">GarageLeads</div>
        <div className="text-[11px] text-muted-foreground">{t("brand_tagline")}</div>
      </div>
    </div>
  );
};

export const AppShell = ({ children }) => {
  const location = useLocation();
  const t = useT();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] flex-col border-r bg-card/60 backdrop-blur lg:flex">
        <Brand />
        <div className="mt-2">
          <NavItems />
        </div>
        <div className="mt-auto space-y-3 p-4">
          <div className="rounded-xl border bg-secondary/40 p-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{t("sidebar_hint_bold")}</span> {t("sidebar_hint")}
          </div>
          <div className="text-center text-[11px] text-muted-foreground">{t("made_by")}</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b bg-background/80 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0">
              <Brand />
              <NavItems onNavigate={() => setOpen(false)} />
              <div className="mt-6 px-5 text-[11px] text-muted-foreground">{t("made_by")}</div>
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Logo />
            </div>
            <span className="font-heading font-bold">GarageLeads</span>
          </div>
        </div>
        <LanguageToggle testId="language-toggle-mobile" />
      </div>

      {/* Desktop top-right language toggle */}
      <div className="fixed right-6 top-5 z-50 hidden lg:block">
        <LanguageToggle testId="language-toggle" />
      </div>

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
};
